import React from "react";
import { Col, Form, Row, Alert, Card } from "react-bootstrap";
import { CustomModal } from "../Common/Modal";
import { Cliente, ProdutoReserva } from "../../types";
import { FormPagamentosData } from "../FormPagamentos/types";
import { formatPrice } from "../../utils/priceUtils";
import { FaCalculator, FaMoneyBill } from "react-icons/fa";
import { InputSpan } from "../Common/InputSpan";

interface ReservaConfirmModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
  cliente: Cliente | null;
  produtos: ProdutoReserva[];
  dataRetiradaISO: string;
  dataDevolucaoISO: string;
  status: string;
  observacoes: string;
  pagamentosData?: FormPagamentosData;
}

const formatDate = (iso: string) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return iso;
  }
};

const ReservaConfirmModal: React.FC<ReservaConfirmModalProps> = ({
  show,
  onHide,
  onConfirm,
  cliente,
  produtos,
  dataRetiradaISO,
  dataDevolucaoISO,
  status,
  observacoes,
  pagamentosData,
}) => {
  const totalProdutos = produtos.reduce(
    (sum, p) => sum + (p.precoTotal || 0),
    0
  );

  const descontoEscopo = pagamentosData?.descontoEscopo || "total";
  const economia =
    descontoEscopo === "entrada"
      ? (pagamentosData?.valorEntrada || 0) -
        (pagamentosData?.valorEntradaComDesconto ||
          pagamentosData?.valorEntrada ||
          0)
      : (pagamentosData?.valorTotal || totalProdutos) -
        (pagamentosData?.valorComDesconto ||
          pagamentosData?.valorTotal ||
          totalProdutos);

  // Helpers e cálculos para exibição
  const getFormaPagamentoLabel = (forma?: string) => {
    if (!forma) return "";
    const formas: { [key: string]: string } = {
      dinheiro: "Dinheiro",
      pix: "PIX",
      cartao_debito: "Cartão Débito",
      cartao_credito: "Cartão Crédito",
      transferencia: "Transferência",
    };
    return formas[forma] || forma;
  };

  const totalBase = pagamentosData?.valorTotal ?? totalProdutos;
  const valorDesconto =
    descontoEscopo === "total"
      ? totalBase - (pagamentosData?.valorComDesconto ?? totalBase)
      : (pagamentosData?.valorEntrada ?? 0) -
        (pagamentosData?.valorEntradaComDesconto ??
          pagamentosData?.valorEntrada ??
          0);

  const valorTotalLiquido =
    descontoEscopo === "total"
      ? pagamentosData?.valorComDesconto ?? totalBase
      : totalBase;

  const valorEntradaExibida =
    descontoEscopo === "entrada"
      ? pagamentosData?.valorEntradaComDesconto ??
        pagamentosData?.valorEntrada ??
        0
      : pagamentosData?.valorEntrada ?? 0;

  const valorTotalPago =
    valorEntradaExibida + (pagamentosData?.valorFormaPagamento2 ?? 0);

  // Dados da 2ª forma com desconto próprio
  const valor2Bruto = pagamentosData?.valorFormaPagamento2 ?? 0;
  const percDesc2 = pagamentosData?.descontoPercentualForma2 ?? 0;
  const valor2ComDesc = pagamentosData?.valorFormaPagamento2ComDesconto ?? valor2Bruto;
  const valorDesc2Forma = Math.max(0, valor2Bruto - valor2ComDesc);
  const totalPagoEntradaLiquido = valorEntradaExibida + valor2ComDesc;

  return (
    <CustomModal
      show={show}
      onHide={onHide}
      headerLabel="Confirmar Reserva"
      size="xl"
      footer={
        <div className="d-flex gap-2 justify-content-end w-100">
          <button className="btn btn-outline-secondary" onClick={onHide}>
            Voltar e editar
          </button>
          <button className="btn btn-success" onClick={onConfirm}>
            Confirmar e salvar
          </button>
        </div>
      }
    >
      <Alert variant="info" className="mb-3 w-">
        Revise os dados abaixo antes de confirmar.
      </Alert>
      <Row>
        <InputSpan
          label="Cliente"
          value={cliente ? `${cliente.nome} (${cliente.telefone})` : ""}
          size={6}
        />
        <InputSpan
          label="Data de Retirada"
          value={formatDate(dataRetiradaISO)}
          readOnly={true}
          size={3}
        />
        <InputSpan
          label="Data de Devolução"
          value={formatDate(dataDevolucaoISO)}
          readOnly={true}
          size={3}
        />
      </Row>

      <Row>
        <InputSpan label="Status" value={status} readOnly={true} size={6} />
      </Row>

      {/* Produtos Selecionados */}
      <Card className="mb-3 shadow-sm">
        <Card.Header className="bg-success text-white">
          <h6 className="mb-0">Produtos Selecionados</h6>
        </Card.Header>
        <Card.Body>
          {produtos && produtos.length > 0 ? (
            <>
              {produtos.map((produto, index) => (
                <div
                  key={`${produto.produtoId}-${produto.produtoTamanho}-${produto.produtoCor}-${index}`}
                  className="border rounded-3 p-3 mb-3 shadow-sm bg-light-subtle"
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center gap-2">
                      {produto.produtoCodigoProduto && (
                        <span
                          className="fw-bold text-primary"
                          style={{ fontSize: "12px", fontFamily: "monospace" }}
                        >
                          [{produto.produtoCodigoProduto}]
                        </span>
                      )}
                      <h6 className="mb-0 text-dark fw-semibold">
                        {produto.produtoTitulo}
                      </h6>
                    </div>
                    <span className="badge bg-primary-subtle text-primary px-3 py-2">
                      Quantidade: {produto.quantidade}
                    </span>
                  </div>

                  <div className="text-muted small mb-2">
                    {produto.produtoTamanho && (
                      <div>
                        <strong>Tamanho:</strong> {produto.produtoTamanho}
                      </div>
                    )}
                    {produto.produtoCor && (
                      <div>
                        <strong>Cor:</strong> {produto.produtoCor}
                      </div>
                    )}
                  </div>

                  {(produto.precoUnitario || produto.precoTotal) && (
                    <div className="small">
                      {produto.precoUnitario && produto.precoUnitario > 0 && (
                        <div className="text-success">
                          {formatPrice(produto.precoUnitario)} cada
                        </div>
                      )}
                      {produto.precoTotal && produto.precoTotal > 0 && (
                        <div className="text-primary fw-semibold">
                          Total do item: {formatPrice(produto.precoTotal)}
                        </div>
                      )}
                    </div>
                  )}

                  {produto.produtoObservacoes && (
                    <div className="mt-2 small text-muted">
                      <strong>Obs. do produto:</strong>{" "}
                      {produto.produtoObservacoes}
                    </div>
                  )}
                </div>
              ))}

              <div className="mt-2 pt-2 border-top text-end">
                <h6 className="text-primary mb-0 fw-bold">
                  Total dos Produtos: {formatPrice(totalProdutos)}
                </h6>
              </div>
            </>
          ) : (
            <div className="text-muted text-center py-4">
              Nenhum produto selecionado
            </div>
          )}
        </Card.Body>
      </Card>

      <Row>
        <InputSpan 
        label="Observações" 
        value={observacoes} 
        readOnly={true}
        size={12}
        />
      </Row>
      <Card className="mb-3 mt-3 shadow-sm">
        <Card.Body className="p-2 p-md-3">
          {pagamentosData ? (
            <>
              <div
                className="border rounded p-3 mb-3"
                style={{ backgroundColor: "#d6ecff" }}
              >
                <h6 className="mb-3">
                  <FaMoneyBill size={20} /> Detalhamento do Pagamento de Entrada
                </h6>

                {/* Detalhamento do Pagamento de Entrada */}
                <Row>
                  <Col md={12}>
                    <div className="mt-3 p-3 bg-white rounded border">
                      <h6 className="text-muted mb-3">
                        <FaCalculator className="me-2" />
                        Resumo do Pagamento de Entrada
                      </h6>
                      <div className="row g-2 small">
                        <div className="col-12">
                          <div className="d-flex justify-content-between">
                            <span>Valor Total:</span>
                            <span className="fw-semibold">
                              {formatPrice(pagamentosData.valorTotal)}
                            </span>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between">
                            <span>
                              {descontoEscopo === "total"
                                ? "Desconto no valor total"
                                : "Desconto no valor da Entrada"}{" "}
                              ({pagamentosData.descontoPercentual}%):
                            </span>
                            <span>- {formatPrice(valorDesconto)}</span>
                          </div>
                        </div>
                        <div className="col-12 border-top pt-2">
                          <div className="d-flex justify-content-between">
                            <span className="fw-semibold">Valor Líquido:</span>
                            <span className="fw-bold">
                              {formatPrice(valorTotalLiquido)}
                            </span>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex justify-content-between">
                            <span>
                              1ª Forma (
                              {getFormaPagamentoLabel(
                                pagamentosData?.formaPagamentoEntrada
                              )}
                              ):
                            </span>
                            <span>- {formatPrice(valorEntradaExibida)}</span>
                          </div>
                        </div>
                        {pagamentosData.usaDuasFormas && (
                          <div className="col-12">
                            <div className="d-flex justify-content-between">
                              <span>
                                2ª Forma (
                                {getFormaPagamentoLabel(
                                  pagamentosData?.formaPagamento2
                                )}
                                ):
                              </span>
                              <span>
                                -{" "}
                                {formatPrice(
                                  pagamentosData.valorFormaPagamento2 || 0
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                        {pagamentosData.usaDuasFormas && percDesc2 > 0 && (
                          <>
                            <div className="col-12">
                              <div className="d-flex justify-content-between">
                                <span>
                                  Desconto 2ª Forma ({percDesc2}%):
                                </span>
                                <span className="text-danger">- {formatPrice(valorDesc2Forma)}</span>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="d-flex justify-content-between">
                                <span className="fw-semibold">Valor cobrado na 2ª forma:</span>
                                <span className="fw-bold">{formatPrice(valor2ComDesc)}</span>
                              </div>
                            </div>
                          </>
                        )}
                        <div className="col-12 border-top pt-2">
                          <div className="d-flex justify-content-between">
                            <span className="fw-semibold">
                              Total Pago (Entrada):
                            </span>
                            <span className="fw-bold text-success">
                              {formatPrice(totalPagoEntradaLiquido)}
                            </span>
                          </div>
                        </div>
                        <div className="col-12 border-top pt-2">
                          <div className="d-flex justify-content-between">
                            <span className="fw-bold">
                              Saldo Devedor (Após Entrada):
                            </span>
                            <span className="fw-bold text-danger">
                              {formatPrice(pagamentosData.saldoDevedor)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Seção de Status de Pagamento Atual */}
              <div
                className="border rounded p-3 mb-3"
                style={{ backgroundColor: "#fff3cd" }}
              >
                <h6 className="mb-3">
                  <FaMoneyBill size={20} /> Status do Pagamento
                </h6>
                <Row>
                  <Col md={12}>
                    <div className="mt-3 p-3 bg-white rounded border">
                      <h6 className="text-muted mb-3">
                        <FaCalculator className="me-2" />
                        Resumo Atual
                      </h6>
                      <div className="row g-2 small">
                        <div className="col-12">
                          <div className="d-flex justify-content-between">
                            <span>✓ Entrada registrada:</span>
                            <span className="fw-semibold text-success">
                              {formatPrice(totalPagoEntradaLiquido)}
                            </span>
                          </div>
                        </div>
                        <div className="col-12 border-top pt-2">
                          <div className="d-flex justify-content-between">
                            <span className="fw-bold">Saldo Devedor:</span>
                            <span className="fw-bold text-danger">
                              {formatPrice(pagamentosData.saldoDevedor)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          ) : (
            <span className="text-muted">Não há dados de pagamento</span>
          )}
        </Card.Body>
      </Card>
    </CustomModal>
  );
};

export default ReservaConfirmModal;
